import type { ContractorDetailResponse, ContractorSearchHit } from '@mollusk/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { api } from '../api/endpoints';
import { permitDetailHref } from '../features/permits/permitDetailHref';

const relation = (x: {
  match_method: string;
  match_confidence: number;
  candidate_count: number;
  is_ambiguous: boolean | null;
}) =>
  `${x.match_method} · ${(x.match_confidence * 100).toFixed(0)}% · ${x.is_ambiguous || x.candidate_count > 1 ? `${x.candidate_count} candidates (ambiguous)` : 'unambiguous'}`;
function SearchHit({ hit, open }: { hit: ContractorSearchHit; open: () => void }) {
  return (
    <ListItem divider secondaryAction={<Button onClick={open}>View details</Button>}>
      <ListItemText
        primary={hit.display_name ?? hit.license_number ?? hit.profile_id}
        secondary={`${hit.authority_id} · ${hit.credential_class ?? 'entity'} · ${hit.status_normalized ?? 'unknown status'}${hit.is_name_match ? ' · name result (not a national merge)' : ' · exact credential result'}`}
      />
    </ListItem>
  );
}
function Profile({ data }: { data: ContractorDetailResponse }) {
  const e = data.entity;
  const title =
    e?.full_business_name ??
    e?.entity_name ??
    data.credentials[0]?.license_number ??
    data.profile_id;
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6">{title}</Typography>
      <Box sx={{ display: 'flex', gap: 1, my: 1, flexWrap: 'wrap' }}>
        <Chip label={e?.authority_id ?? data.credentials[0]?.authority_id ?? 'unknown authority'} />
        <Chip
          label={
            e?.entity_status_normalized ??
            data.credentials[0]?.status_normalized ??
            'unknown status'
          }
        />
        <Chip label={`${data.profile_kind} profile`} />
      </Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Status is publisher-observed. Absence from a later snapshot is not evidence that a
        credential was revoked.
      </Alert>
      {e && (
        <Typography variant="body2">
          Aliases: {[e.business_name_2, e.dba_name].filter(Boolean).join(' · ') || '—'}
          <br />
          Location/contact:{' '}
          {[e.mailing_address, e.city, e.state, e.postal_code, e.phone]
            .filter(Boolean)
            .join(' · ') || '—'}
          <br />
          Observed {e.status_observed_at ?? 'unknown'} · publisher as of{' '}
          {e.publisher_as_of ?? 'unknown'}
        </Typography>
      )}
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1">Credentials ({data.credentials.length})</Typography>
      <List dense>
        {data.credentials.map((c) => (
          <ListItem key={c.credential_id}>
            <ListItemText
              primary={`${c.license_number ?? 'No native number'} — ${c.credential_class_description ?? c.credential_class ?? c.license_type ?? 'Credential'}`}
              secondary={`${c.authority_id} · ${c.status_normalized ?? c.status ?? 'unknown'} · effective ${c.effective_date ?? '—'} · expires ${c.expiration_date ?? '—'} · observed ${c.status_observed_at ?? '—'}`}
            />
          </ListItem>
        ))}
      </List>
      <Typography variant="subtitle1">Qualifiers / principals ({data.people.length})</Typography>
      <List dense>
        {data.people.map((p) => (
          <ListItem key={p.person_id}>
            <ListItemText
              primary={
                p.person_name ??
                ([p.first_name, p.last_name].filter(Boolean).join(' ') || 'Unnamed person')
              }
              secondary={`${p.relationship_normalized ?? p.relationship_type ?? p.title ?? 'relationship unknown'} · ${p.status_normalized ?? p.status ?? 'status unknown'}`}
            />
          </ListItem>
        ))}
      </List>
      <Typography variant="subtitle1">Assurances ({data.assurances.length})</Typography>
      <List dense>
        {data.assurances.map((a) => (
          <ListItem key={a.assurance_id}>
            <ListItemText
              primary={a.assurance_type_normalized ?? a.assurance_type ?? 'Assurance'}
              secondary={`${a.provider_name ?? 'provider unpublished'} · ${a.status_normalized ?? a.status ?? 'status unknown'} · ${a.effective_date ?? '—'} to ${a.expiration_date ?? '—'}${a.amount == null ? '' : ` · $${a.amount.toLocaleString()}`}`}
            />
          </ListItem>
        ))}
      </List>
      <Typography variant="subtitle1">Related activity ({data.activities.length})</Typography>
      <List dense>
        {data.activities.map((a) => (
          <ListItem
            key={a.link_id}
            secondaryAction={
              <Button
                href={
                  a.activity_dataset === 'planning'
                    ? `/planning-detail?caseId=${encodeURIComponent(a.activity_key)}`
                    : permitDetailHref(a.activity_source_id, a.activity_key)
                }
              >
                Open activity
              </Button>
            }
          >
            <ListItemText
              primary={`${a.activity_dataset}: ${a.activity_key}`}
              secondary={`${a.jurisdiction_state ?? ''} · ${a.activity_date ?? 'undated'} · ${relation(a)} · temporal ${a.valid_on_activity_date == null ? 'unknown' : a.valid_on_activity_date ? 'valid' : 'invalid'}`}
            />
          </ListItem>
        ))}
      </List>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1">
        Internal-only source observations ({data.observations.length})
      </Typography>
      <Alert severity="warning">
        These provenance/mapper diagnostics are restricted to this authenticated internal QA tool.
        Raw records and sensitive policy/bond numbers are not exposed.
      </Alert>
      <List dense>
        {data.observations.map((o) => (
          <ListItem key={o.observation_id}>
            <ListItemText
              primary={`${o.source_id} · ${o.map_outcome ?? 'mapped/unknown'}`}
              secondary={`${o.observed_status ?? 'status unpublished'} · observed ${o.status_observed_at ?? '—'} · fetched ${o.fetched_at ?? '—'}${o.reject_reason ? ` · reject: ${o.reject_reason}` : ''}`}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
function ContractorDetailPage() {
  const routeSearch = Route.useSearch();
  const detailRef = useRef<HTMLDivElement>(null);
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [state, setState] = useState('');
  const [authority, setAuthority] = useState('');
  const [selected, setSelected] = useState<ContractorSearchHit | null>(
    routeSearch.id && routeSearch.kind
      ? {
          profile_kind: routeSearch.kind,
          profile_id: routeSearch.id,
          authority_id: '',
          display_name: null,
          license_number: null,
          credential_class: null,
          status_normalized: null,
          jurisdiction_state: null,
          source_id: '',
          is_name_match: false,
        }
      : null,
  );
  const search = useQuery({
    queryKey: ['contractor-search', query, state, authority],
    queryFn: () => api.contractorSearch(query, state || undefined, authority || undefined),
    enabled: query.length >= 2,
  });
  const detail = useQuery({
    queryKey: ['contractor-detail', selected?.profile_kind, selected?.profile_id],
    queryFn: () => {
      if (!selected) throw new Error('contractor profile not selected');
      return api.contractorDetail(selected.profile_kind, selected.profile_id);
    },
    enabled: Boolean(selected),
  });
  useEffect(() => {
    if (selected) detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [selected]);
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Contractor detail
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Search exact credential number with authority context, authority-scoped entity name, or
        qualifier/person name. Similar names remain separate results and are never merged nationally
        in the browser.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          Try a credential with linked permit activity:
        </Typography>
        {['CBC1267632', '1082165', '1145152'].map((term) => (
          <Button
            key={term}
            size="small"
            variant="outlined"
            onClick={() => {
              setInput(term);
              setQuery(term);
              setSelected(null);
            }}
          >
            {term}
          </Button>
        ))}
      </Box>
      <Paper sx={{ p: 2 }}>
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            setQuery(input);
            setSelected(null);
          }}
          sx={{ display: 'flex', gap: 1 }}
        >
          <TextField
            fullWidth
            size="small"
            label="Credential, entity, or person"
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
          <TextField
            size="small"
            label="State"
            value={state}
            onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
            sx={{ width: 100 }}
          />
          <TextField
            size="small"
            label="Authority ID"
            value={authority}
            onChange={(e) => setAuthority(e.target.value)}
            sx={{ width: 220 }}
          />
          <Button type="submit" variant="contained" disabled={input.trim().length < 2}>
            Search
          </Button>
        </Box>
        {(search.isFetching || detail.isFetching) && (
          <CircularProgress size={24} aria-label="Loading contractor data" sx={{ mt: 2 }} />
        )}
        {search.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Contractor search failed.
          </Alert>
        )}
        {query && !search.isPending && !search.data?.results.length && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No contractor profiles found.
          </Alert>
        )}
        {!selected && (
          <List aria-label="Contractor search results">
            {search.data?.results.map((h) => (
              <SearchHit
                key={`${h.profile_kind}-${h.profile_id}`}
                hit={h}
                open={() => setSelected(h)}
              />
            ))}
          </List>
        )}
        {selected && (
          <Box ref={detailRef} sx={{ mt: 2 }}>
            {query && (
              <Button variant="outlined" onClick={() => setSelected(null)}>
                Back to search results
              </Button>
            )}
            {detail.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Contractor profile failed.
              </Alert>
            )}
            {detail.data && <Profile data={detail.data} />}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
export const Route = createFileRoute('/contractor-detail')({
  validateSearch: z.object({
    kind: z.enum(['entity', 'credential']).optional(),
    id: z.string().optional(),
  }),
  component: ContractorDetailPage,
});
